import { useState } from 'react';

interface AttachmentEncoded {
	fileName: string;
	mimeType: string;
	contentBase64: string | ArrayBuffer | null;
}

type MailOptionsBase = {
	replyToMail: string;
	subject: string;
	message: string;
	attachments?: FileList;
};

type MailOptions =
	| (MailOptionsBase & {
			recaptchaToken: string;
			verifyRecaptcha: true;
	  })
	| (MailOptionsBase & {
			recaptchaToken?: string;
			verifyRecaptcha?: false;
	  });

function toBase64(file: File): Promise<string | ArrayBuffer | null> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.readAsDataURL(file);
		reader.onload = () => resolve(reader.result);
		reader.onerror = (error) => reject(error);
	});
}

export const useMail = () => {
	const [isSending, setIsSending] = useState(false);

	const sendMail = async (
		{ replyToMail, subject, message, attachments, recaptchaToken, verifyRecaptcha }: MailOptions,
		isOneOfMany: boolean = false,
	): Promise<void> => {
		isOneOfMany || setIsSending(true);

		const attachmentsEncoded = attachments
			? await Promise.all(
					Array.from(attachments).map(async (attachment) => {
						const contentBase64 = await toBase64(attachment);
						return {
							fileName: attachment.name,
							mimeType: attachment.type,
							contentBase64,
						} as AttachmentEncoded;
					}),
				)
			: [];

		const body = JSON.stringify({
			email: replyToMail,
			subject,
			message,
			attachments: attachmentsEncoded,
			recaptchaToken,
			verifyRecaptcha: verifyRecaptcha || false,
		});

		try {
			const response = await fetch(`/.netlify/functions/send-email`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body,
			});

			if (!response.ok) throw Error();
		} catch (error) {
			throw error;
		} finally {
			isOneOfMany || setIsSending(false);
		}
	};

	const sendMails = async (mailsOptions: MailOptions[]): Promise<void> => {
		setIsSending(true);

		try {
			await Promise.all(
				// TODO: recaptcha is currently not implemented for sending multiple mails
				mailsOptions.map(
					async (mailOptions) => await sendMail({ ...mailOptions, verifyRecaptcha: false }, true),
				),
			);
		} catch (error) {
			throw error;
		} finally {
			setIsSending(false);
		}
	};

	return { isSending, sendMail, sendMails };
};
